/**
 * FileUploadZone - Composant de drag & drop pour upload de fichiers
 * Design System: Dashboard context (bg-white, text-gray-900)
 */

import { useCallback, useState } from 'react';
import { Upload, FileText, X, AlertCircle } from 'lucide-react';
import type { ImportType } from '../types/import.types';

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void;
  selectedType: ImportType;
  acceptedFormats?: string[];
  maxSize?: number; // in MB
  disabled?: boolean;
}

const DEFAULT_ACCEPTED_FORMATS = ['.csv', '.xlsx', '.xls', '.txt', '.fec', '.pdf', '.ofx', '.qif', '.xml', '.json', '.mt940', '.sta'];
const DEFAULT_MAX_SIZE = 50; // 50MB

export function FileUploadZone({
  onFileSelect,
  selectedType,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS,
  maxSize = DEFAULT_MAX_SIZE,
  disabled = false,
}: FileUploadZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSize) {
        return `Le fichier est trop volumineux (${fileSizeMB.toFixed(1)} MB). Maximum: ${maxSize} MB`;
      }

      // Check extension
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedFormats.includes(ext)) {
        return `Format non supporté (${ext}). Formats acceptés: ${acceptedFormats.join(', ')}`;
      }

      return null;
    },
    [acceptedFormats, maxSize]
  );

  const handleFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setSelectedFile(null);
        return;
      }

      setError(null);
      setSelectedFile(file);
      onFileSelect(file);
    },
    [validateFile, onFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled) return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [disabled, handleFile]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center
          transition-all duration-300 ease-in-out
          ${
            disabled
              ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
              : isDragOver
              ? 'bg-teal-50 border-teal-400 scale-[1.02]'
              : 'bg-white border-gray-300 hover:border-teal-400 hover:bg-gray-50 cursor-pointer'
          }
        `}
      >
        <input
          type="file"
          onChange={handleInputChange}
          accept={acceptedFormats.join(',')}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <div className="flex flex-col items-center gap-4">
          <div
            className={`
              p-4 rounded-full transition-colors duration-300
              ${isDragOver ? 'bg-teal-100' : 'bg-gray-100'}
            `}
          >
            <Upload
              className={`w-8 h-8 ${isDragOver ? 'text-teal-600' : 'text-gray-400'}`}
            />
          </div>

          <div>
            <p className="text-gray-900 font-medium">
              {isDragOver ? 'Relâchez pour importer' : 'Glissez votre fichier ici'}
            </p>
            <p className="text-gray-500 text-sm mt-1">
              ou <span className="text-teal-600 font-medium">parcourez vos fichiers</span>
            </p>
          </div>

          <div className="text-xs text-gray-400">
            Formats: {acceptedFormats.join(', ')} | Max: {maxSize} MB
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Selected File */}
      {selectedFile && !error && (
        <div className="flex items-center justify-between p-4 bg-teal-50 border border-teal-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <FileText className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-gray-900 font-medium">{selectedFile.name}</p>
              <p className="text-gray-500 text-sm">{formatFileSize(selectedFile.size)}</p>
            </div>
          </div>
          <button
            onClick={handleRemoveFile}
            className="p-2 hover:bg-teal-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      )}

      {/* Import Type Info */}
      <div className="text-sm text-gray-500">
        Type d'import sélectionné:{' '}
        <span className="font-medium text-gray-700">{selectedType}</span>
      </div>
    </div>
  );
}
